import { getDialectAdapter } from '@/database/dialectRegistry'
import type { DataTypeDefinition } from '@/database/dialectTypes'
import type { MorphSelectOption } from '@/components/ui/MorphSelect'
import type { DatabaseDialect } from '@/types'

export const DATABASE_FIELD_TYPES: MorphSelectOption[] = [
  { label: 'VARCHAR',   value: 'varchar',   metadata: { aliases: ['var', 'string'] } },
  { label: 'TEXT',      value: 'text' },
  { label: 'CHAR',      value: 'char' },

  { label: 'INT',       value: 'int',       metadata: { aliases: ['integer'] } },
  { label: 'BIGINT',    value: 'bigint' },
  { label: 'SMALLINT',  value: 'smallint' },
  { label: 'SERIAL',    value: 'serial' },
  { label: 'BIGSERIAL', value: 'bigserial' },

  { label: 'DECIMAL',   value: 'decimal' },
  { label: 'NUMERIC',   value: 'numeric' },
  { label: 'FLOAT',     value: 'float' },
  { label: 'DOUBLE',    value: 'double' },

  { label: 'BOOLEAN',   value: 'boolean',   metadata: { aliases: ['bool'] } },

  { label: 'DATE',      value: 'date' },
  { label: 'TIME',      value: 'time' },
  { label: 'DATETIME',  value: 'datetime',  metadata: { aliases: ['timestamp'] } },
  { label: 'TIMESTAMP', value: 'timestamp', metadata: { aliases: ['time'] } },

  { label: 'UUID',      value: 'uuid' },
  { label: 'JSON',      value: 'json' },
  { label: 'JSONB',     value: 'jsonb' },

  { label: 'ENUM',      value: 'enum' },
  { label: 'BLOB',      value: 'blob' },
]

function normalizeTypeKey(value: string) {
  return value.trim().toLowerCase().replace(/\s*\(.*\)/, '').replace(/\s+/g, ' ')
}

function buildAliases(dataType: DataTypeDefinition, baseOption?: MorphSelectOption) {
  return Array.from(new Set([
    normalizeTypeKey(dataType.value),
    dataType.label.toLowerCase(),
    dataType.logicalType.toLowerCase(),
    baseOption?.value,
    baseOption?.label.toLowerCase(),
    ...(Array.isArray(baseOption?.metadata?.aliases) ? baseOption.metadata.aliases as string[] : []),
    ...(dataType.logicalType === 'boolean' ? ['bool'] : []),
    ...(dataType.logicalType === 'datetime' ? ['time', 'timestamp', 'datetime'] : []),
  ].filter(Boolean)))
}

function toDialectOption(dataType: DataTypeDefinition): MorphSelectOption {
  const baseOption = DATABASE_FIELD_TYPES.find((option) => option.value === normalizeTypeKey(dataType.value))
  return {
    label: dataType.value,
    value: dataType.value,
    metadata: {
      logicalType: dataType.logicalType,
      aliases: buildAliases(dataType, baseOption),
    },
  }
}

export function getDatabaseFieldTypeOptions(dialect?: DatabaseDialect): MorphSelectOption[] {
  if (!dialect) {
    return DATABASE_FIELD_TYPES.map((option) => ({
      ...option,
      value: option.label,
      metadata: {
        ...option.metadata,
        aliases: buildAliases(
          {
            value: option.label,
            label: option.label,
            logicalType: normalizeTypeKey(option.value) as DataTypeDefinition['logicalType'],
          },
          option,
        ),
      },
    }))
  }

  return getDialectAdapter(dialect).dataTypes.map(toDialectOption)
}
